import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { getSelectedExhibitionId, getSelfRegistration, isUserLoggedIn, selectIsFirstLoadDone } from '@app/app.reducer';
import { FieldKey } from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { FormFields, SetFormFields } from '@core/data-access/models/form/form-store.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { Person } from '@core/data-access/models/person.model';
import { BaseRoute, StepRoute } from '@core/data-access/models/routes.model';
import { InitializationService } from '@core/data-access/models/service.model';
import { FormMediator } from '@core/data-access/services/form/form-mediator.service';
import { BuyerFormsSyncActions, FormFieldSyncActions } from '@core/data-access/store/form/form.actions';
import { selectSavedBuyer } from '@core/features/account/features/saved-ticket-holders/store/saved-ticket-holder.selectors';
import { BuyerInformationFormState } from '@core/features/buyer-information/models/buyer-information-form.model';
import { selectIsBuyerInformationFormPrefilled } from '@core/features/buyer-information/store/buyer-information.selectors';
import {
  mapGermanBuyerInformationUrlParamsToFormFieldNames,
  mapQueryParamGenderKeyValueToFormFieldValue
} from '@core/utils/data-mappers';
import { select, Store } from '@ngrx/store';
import { every, intersection, isEmpty, isEqual, isNil, isNull, isObject, keys, omitBy, reduce } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  Observable,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FormPrefillService implements InitializationService {
  readonly #destroyRef = inject(DestroyRef);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #store = inject(Store);
  readonly #formMediator = inject(FormMediator);

  #prefillBuyerInformationForm$ = new BehaviorSubject<boolean | null>(null);
  #isCopyBuyerInformationModalOpen$ = new BehaviorSubject<boolean>(false);

  #buyerInformationFormState$: Observable<BuyerInformationFormState> = toObservable(
    this.#formMediator.getFormState(FormName.BuyerInformation)
  );

  init() {
    this.prefillBuyerInformationFormWithSavedBuyerTicketHolderSubscription();
    this.prefillBuyerInformationWithUrlParametersSubscription();
  }

  setBuyerInformationFormPrefill(prefillBuyerInformationForm: boolean, exhibitionId: number) {
    if (prefillBuyerInformationForm) {
      this.#router.navigate([BaseRoute.WebShop, exhibitionId, StepRoute.BuyerInformation]);
    }

    this.#isCopyBuyerInformationModalOpen$.next(false);

    this.#prefillBuyerInformationForm$.next(prefillBuyerInformationForm);
  }

  get isCopyBuyerInformationModalOpen() {
    return toSignal(this.#isCopyBuyerInformationModalOpen$, { initialValue: false });
  }

  private prefillBuyerInformationFormWithSavedBuyerTicketHolderSubscription() {
    return this.#store
      .pipe(
        select(isUserLoggedIn),
        first((isUserLoggedIn) => isUserLoggedIn),
        switchMap(() =>
          combineLatest({
            isFirstLoadDone: this.#store.select(selectIsFirstLoadDone),
            prefillBuyerInformationForm: this.#prefillBuyerInformationForm$
          }).pipe(
            debounceTime(250),
            withLatestFrom(
              this.#isCopyBuyerInformationModalOpen$,
              this.#store.select(selectSavedBuyer),
              this.#store.select(getSelectedExhibitionId),
              this.#store.select(selectIsBuyerInformationFormPrefilled)
            ),
            filter(([_, isCopyBuyerInformationModalOpen]) => !isCopyBuyerInformationModalOpen),
            tap(([_, __, savedBuyer, selectedExhibitionId, isBuyerInformationFormPrefilled]) => {
              if (isNull(selectedExhibitionId)) {
                return;
              }

              if (isNil(savedBuyer) || isNull(isBuyerInformationFormPrefilled)) {
                this.#prefillBuyerInformationForm$.next(null);
              }
            }),
            filter(
              ([_, __, savedBuyer, selectedExhibitionId, isBuyerInformationFormPrefilled]) =>
                !isNil(savedBuyer) && !isNull(selectedExhibitionId) && isNull(isBuyerInformationFormPrefilled)
            ),
            map(([{ prefillBuyerInformationForm }, _, savedBuyer]) => ({
              prefillBuyerInformationForm,
              savedBuyer
            })),
            distinctUntilChanged(isEqual),
            withLatestFrom(this.#buyerInformationFormState$, this.#store.select(getSelfRegistration)),
            map(([{ prefillBuyerInformationForm, savedBuyer }, buyerInformationFormState, isSelfRegistration]) => {
              const buyerInformationFormValues = buyerInformationFormState?.form?.formGroup?.value;
              const areEmptyBuyerInformationFormFieldValues = every(
                omitBy(buyerInformationFormValues, isObject),
                isEmpty
              );

              const prefillBuyerInformationFormWithoutOpeningModal =
                (areEmptyBuyerInformationFormFieldValues && isNull(prefillBuyerInformationForm)) || isSelfRegistration;
              const openBuyerInformationFormPrefillModal =
                !areEmptyBuyerInformationFormFieldValues && isNull(prefillBuyerInformationForm) && !isSelfRegistration;

              return {
                savedBuyer,
                prefillBuyerInformationForm,
                prefillBuyerInformationFormWithoutOpeningModal,
                openBuyerInformationFormPrefillModal
              };
            }),
            tap(
              ({
                prefillBuyerInformationForm,
                prefillBuyerInformationFormWithoutOpeningModal,
                openBuyerInformationFormPrefillModal
              }) => {
                if (prefillBuyerInformationForm) {
                  return;
                }

                if (prefillBuyerInformationFormWithoutOpeningModal) {
                  this.#prefillBuyerInformationForm$.next(true);

                  return;
                }

                if (openBuyerInformationFormPrefillModal) {
                  this.#isCopyBuyerInformationModalOpen$.next(true);
                }
              }
            ),
            filter(({ prefillBuyerInformationForm }) => !isNull(prefillBuyerInformationForm))
          )
        )
      )
      .subscribe(({ savedBuyer: { person, formFields }, prefillBuyerInformationForm }) => {
        this.#store.dispatch(
          BuyerFormsSyncActions.setBuyerInformationFormPrefill({ prefillBuyerInformationForm, person, formFields })
        );
      });
  }

  private prefillBuyerInformationWithUrlParametersSubscription() {
    return combineLatest({
      queryParamMap: this.#activatedRoute.queryParamMap,
      buyerInformationFormState: this.#buyerInformationFormState$
    })
      .pipe(
        first(({ buyerInformationFormState }) => {
          const buyerPersonUuid = buyerInformationFormState?.person?.uuid;
          const buyerInformationFormValues = buyerInformationFormState?.form?.formGroup?.value;
          const areEmptyBuyerInformationFormFieldValues = every(omitBy(buyerInformationFormValues, isObject), isEmpty);

          return !isNil(buyerPersonUuid) && areEmptyBuyerInformationFormFieldValues;
        }),
        map(({ queryParamMap, buyerInformationFormState: { person } }) => ({
          queryParamMap,
          person,
          mappedGermanUrlParamsToFormFieldNames: mapGermanBuyerInformationUrlParamsToFormFieldNames()
        })),
        filter(
          ({ queryParamMap, mappedGermanUrlParamsToFormFieldNames }) =>
            !isEmpty(intersection(queryParamMap.keys, keys(mappedGermanUrlParamsToFormFieldNames)))
        ),
        map(({ queryParamMap, person, mappedGermanUrlParamsToFormFieldNames }) => {
          const formFields = reduce(
            queryParamMap.keys,
            (formFields, paramKey) => {
              if (!isNil(mapGermanBuyerInformationUrlParamsToFormFieldNames[paramKey])) {
                return formFields;
              }

              const queryParamFormFieldKey = mappedGermanUrlParamsToFormFieldNames[paramKey];
              const queryParamKeyValue = queryParamMap.get(paramKey);

              if (queryParamFormFieldKey === FieldKey.Gender) {
                formFields[queryParamFormFieldKey] = {
                  value: mapQueryParamGenderKeyValueToFormFieldValue(queryParamKeyValue),
                  isQuestionnaire: false
                };

                return formFields;
              }

              if (queryParamFormFieldKey === FieldKey.Email) {
                formFields[FieldKey.MailConfirm] = {
                  value: queryParamKeyValue,
                  isQuestionnaire: false
                };
              }

              formFields[queryParamFormFieldKey] = {
                value: queryParamKeyValue,
                isQuestionnaire: false
              };

              return formFields;
            },
            {} as FormFields
          );

          return {
            person,
            formFields
          };
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(({ person, formFields }) => this.fillBuyerInformationFormFieldsWithUrlParameters(person, formFields));
  }

  private fillBuyerInformationFormFieldsWithUrlParameters(person: Person, formFields: FormFields) {
    const setFormFields: SetFormFields = {
      person,
      originForm: FormName.BuyerInformation,
      syncForms: [FormName.BuyerInformation, FormName.TicketHolder],
      formFields
    };

    this.#store.dispatch(FormFieldSyncActions.setFormFields(setFormFields));
  }
}
